import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Grid, Typography, Button, Box } from '@mui/material'
import { DataGridPremium, GridColumnMenu, GridColumnMenuProps, GridEventListener, useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid-premium'
import { useEffect, useState } from 'react'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadDialog from './UploadDialogBox'
import DeleteConfirmationDialog from './DeleteDialog'
import { downloadOriginalGeochemicalDataFile } from '../../libs/api/geochemical-files/GeochemicalFilesAPI'
import { useNotificationContext } from '../../context/NotificationContext'

export interface DataManagerGridProps {
    gridTitle: string
    loading: boolean
    files: any
    onDeleteFile: (fileId: string) => void
    onRefreshFiles: () => Promise<void> // Add this line
}

/**
 * The Project Management Grid component
 */
export default function ManageDataGrid(props: DataManagerGridProps) {
    const { gridTitle, loading, files, onDeleteFile, onRefreshFiles } = props
    const apiRef = useGridApiRef()

    // State to control the visibility of the UploadDialog
    const [openUploadDialog, setOpenUploadDialog] = useState<boolean>(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [fileToDelete, setFileToDelete] = useState(null)

    const handleDeleteConfirmation = (file) => {
        setOpenDeleteDialog(true)
        setFileToDelete(file)
    }

    const handleDelete = async () => {
        if (fileToDelete) {
            setOpenDeleteDialog(false)
            await onDeleteFile(fileToDelete.id)
            // No need to manage files state here, as it should be managed in DataManagerPage
        }
    }

    const builtColumns = buildColumns(handleDeleteConfirmation)

    const onRowClick: GridEventListener<'rowClick'> = (params) => {
        apiRef.current.toggleDetailPanel(params.id)
    }

    function CustomColumnMenu(props: GridColumnMenuProps) {
        return (
            <GridColumnMenu
                {...props}
                slots={{
                    columnMenuGroupingItem: null,
                    columnMenuPinningItem: null,
                    columnMenuColumnsItem: null,
                }}
            />
        )
    }

    return (
        <Grid>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 0',
                }}
            >
                <Typography variant="h4">{gridTitle}</Typography>
                <Button sx={{ textTransform: 'none' }} variant="contained" onClick={() => setOpenUploadDialog(true)}>
                    Upload File
                </Button>
            </Box>
            <UploadDialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} onRefreshFiles={onRefreshFiles} />
            <DataGridPremium
                getRowId={(r) => r.id}
                apiRef={apiRef}
                pagination
                autoHeight
                pageSizeOptions={[25, 50, 100]}
                sortingOrder={['asc', 'desc']}
                rows={files}
                loading={loading}
                columns={builtColumns}
                initialState={initialState}
                disableRowSelectionOnClick
                onRowClick={onRowClick}
                slots={{
                    detailPanelExpandIcon: KeyboardArrowDown,
                    detailPanelCollapseIcon: KeyboardArrowUp,
                    columnMenu: CustomColumnMenu,
                    noRowsOverlay: () => <NoProjectsOverlay />,
                }}
                sx={{
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '.MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                    '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                    },
                    '& .project-name': {
                        fontWeight: 'bold',
                    },
                    borderTop: 'none',
                    borderRight: 'none',
                    borderLeft: 'none',
                }}
                getDetailPanelHeight={() => 'auto'}
            />
            <DeleteConfirmationDialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} onConfirm={handleDelete} />
        </Grid>
    )
}

/**
 * Component to render as fallback when no data is available
 */
const NoProjectsOverlay = () => {
    const [show, setShow] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 300)
        return () => clearTimeout(timeout)
    }, [])

    if (!show) return null
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
            }}
        >
            <p>No projects found</p>
        </div>
    )
}

/**
 * The datagrid columns setup
 */
const buildColumns = (handleDeleteConfirmation: (file: any) => void) => {
    return [
        {
            field: 'fileName',
            headerName: 'File Name',
            flex: 2,
            availableAggregationFunctions: [],
        },
        {
            field: 'associatedSampleIDs',
            headerName: 'Associated Sample IDs',
            flex: 2,
            availableAggregationFunctions: [],
        },
        {
            field: 'vendor',
            headerName: 'Vendor',
            flex: 1,
            availableAggregationFunctions: [],
        },
        {
            field: 'uploadDate',
            headerName: 'Uploaded On',
            flex: 2,
            availableAggregationFunctions: [],
        },
        {
            field: 'actions',
            type: 'actions',
            flex: 1,
            getActions: (params) => [
                <GridActionsCellItem
                    key="downloadAction"
                    icon={<DownloadIcon />}
                    onClick={async () => {
                        const response = await downloadOriginalGeochemicalDataFile(params.id)
                        if (response.succeeded) {
                            const url = URL.createObjectURL(response.data)
                            const link = document.createElement('a')
                            link.href = url
                            link.setAttribute('download', params.row.fileName)
                            document.body.appendChild(link)
                            link.click()
                            document.body.removeChild(link)
                        } else {
                            useNotificationContext().openNotification(response.errors.message, response.errors.severity)
                        }
                    }}
                    label="Download"
                />,
                <GridActionsCellItem key="deleteAction" icon={<DeleteIcon />} onClick={() => handleDeleteConfirmation(params.row)} label="Delete" />,
            ],
        },
    ]
}

/**
 * The datagrid initial state
 */
const initialState = {
    pagination: {
        paginationModel: { pageSize: 50, page: 0 },
    },
}
